import { addRxPlugin, createRxDatabase, removeRxDatabase, RxDocument, RxStorage } from 'rxdb';
import { getRxStorageIndexedDB } from 'rxdb-premium/plugins/storage-indexeddb';
import { getAjv, wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv';
import { RxDBDevModePlugin, disableWarnings } from 'rxdb/plugins/dev-mode';
import { RxDBCleanupPlugin } from 'rxdb/plugins/cleanup';
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election';
import { RxDBLocalDocumentsPlugin } from 'rxdb/plugins/local-documents';
import { RxDBMigrationSchemaPlugin } from 'rxdb/plugins/migration-schema';
import addAjvFormats from 'ajv-formats';

import type { RxNoodleCollections, RxNoodleDB, RxNoodleDBCollectionsKeys } from './rxdb.types';
import { qreTemplateOfQreSchema } from './schemas/qreTemplateOfQre.schema';
import { qreFieldTemplateSchema } from './schemas/qreFieldTemplate.schema';
import { CURRENT_RESET_NUMBER, RESET_DB_KEY, RX_DB_NAME } from './rxdb.constants';

const isDevMode: boolean = process.env.NODE_ENV === 'development';

if (isDevMode) {
  disableWarnings();
  addRxPlugin(RxDBDevModePlugin);
}

addRxPlugin(RxDBLeaderElectionPlugin);
addRxPlugin(RxDBCleanupPlugin);
addRxPlugin(RxDBLocalDocumentsPlugin);
addRxPlugin(RxDBMigrationSchemaPlugin);

if (typeof window !== 'undefined') {
  const ajv = getAjv();
  // @ts-expect-error - Known type mismatch between ajv versions
  addAjvFormats(ajv);
}

let rxNoodleDB: RxNoodleDB;

function getRxDevStorage(): RxStorage<unknown, unknown> {
  return wrappedValidateAjvStorage({
    storage: getRxStorageIndexedDB(),
  });
}

function getRxProdStorage(): RxStorage<unknown, unknown> {
  return getRxStorageIndexedDB();
}

function getRxStorage(): RxStorage<unknown, unknown> {
  return isDevMode ? getRxDevStorage() : getRxProdStorage();
}

function defaultMigrationStrategy(_doc: RxDocument): RxDocument | null {
  return null;
}

async function initializeCollections(): Promise<boolean> {
  try {
    await rxNoodleDB.addCollections({
      qreFieldTemplate: {
        migrationStrategies: {
          1: defaultMigrationStrategy,
          2: defaultMigrationStrategy,
          3: defaultMigrationStrategy,
          4: defaultMigrationStrategy,
        },
        schema: qreFieldTemplateSchema,
      },
      qreTemplateOfQre: {
        migrationStrategies: {
          1: defaultMigrationStrategy,
        },
        schema: qreTemplateOfQreSchema,
      },
    });

    return true;
  } catch (error) {
    console.error('Error initializing collections', error);
    return false;
  }
}

/**
 * Purges the data of the collections.
 *
 * @returns {Promise<boolean>} - True if the data was purged, false if an error occurred.
 */
export async function purgeCollectionsData(): Promise<boolean> {
  try {
    const collections: RxNoodleDBCollectionsKeys[] = ['qreFieldTemplate', 'qreTemplateOfQre'];

    await Promise.all(
      collections.map(async collection => {
        await rxNoodleDB[collection].find().remove();
        await rxNoodleDB[collection].cleanup(0);
      }),
    );

    return true;
  } catch (error) {
    console.error('Error purging collections data', error);
    return false;
  }
}

function shouldResetRxNoodleDB(): boolean {
  try {
    const hasBeenReset = localStorage.getItem(RESET_DB_KEY);
    const shouldReset = hasBeenReset !== CURRENT_RESET_NUMBER;
    return shouldReset;
  } catch (error) {
    console.error('Error checking reset status', error);
    return false;
  }
}

function markRxNoodleDBAsReset(): void {
  try {
    localStorage.setItem(RESET_DB_KEY, CURRENT_RESET_NUMBER);
  } catch (error) {
    console.error('Error marking reset status', error);
  }
}

/**
 * Initializes the RxNoodleDB instance.
 *
 * @returns {Promise<boolean>} - True if the initialization was successful, false if an error occurred.
 */
async function initializeRxNoodleDB(): Promise<boolean> {
  try {
    if (shouldResetRxNoodleDB()) {
      await removeRxDatabase(RX_DB_NAME, getRxStorage());
      markRxNoodleDBAsReset();
    }

    rxNoodleDB = await createRxDatabase<RxNoodleCollections>({
      cleanupPolicy: {
        minimumDeletedTime: 0,
      },
      localDocuments: true,
      name: RX_DB_NAME,
      storage: getRxStorage(),
    });

    const successfullyAddedCollections = await initializeCollections();
    if (!successfullyAddedCollections) return false;

    return true;
  } catch (error) {
    console.error('Error initializing RxNoodleDB', error);
    return false;
  }
}

// eslint-disable-next-line import/prefer-default-export
export async function getNoodleDb(): Promise<RxNoodleDB> {
  if (!rxNoodleDB) {
    await initializeRxNoodleDB();
  }

  return rxNoodleDB;
}
